/**
 * The folders of «Переместить в папку», as the app's MoveRoomToFolder lists them (RoomTypesList: «Выбери тип комнаты»,
 * the floor plan first, the room types after it, the project's own one marked). A radio group: the arrows move the
 * choice and the focus together, Home and End go to the ends, a click or a tap chooses.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { PROJECT_FOLDERS } from '../../state/projectFolders';
import { Icon } from '../Icon';
import { t } from '../../i18n/I18n';

export function FolderChoice({ value, current, onChange }: { value: string | null; current: string | null; onChange: (value: string) => void }) {
  const group = useRef<HTMLDivElement>(null), titleId = useId();
  const at = PROJECT_FOLDERS.findIndex(folder => folder.id === value);
  /** Whether more of the list lies below, on a window too short for all of it: its bottom edge fades then. */
  const [more, setMore] = useState(false);
  const measure = () => { const list = group.current; if (list) setMore(list.scrollTop + list.clientHeight < list.scrollHeight - 2); };
  // The list opens on the folder the project is in, wherever it is in it: a frame later, once the dialog around it
  // shows (its own effect runs after this one), and by scrolling the list alone, never the page under the dialog.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const list = group.current, option = list?.querySelector('[aria-checked=true]');
      if (list && option) {
        const below = option.getBoundingClientRect().bottom - list.getBoundingClientRect().bottom;
        if (below > 0) list.scrollTop += below + 12;
      }
      measure();
    });
    addEventListener('resize', measure);
    return () => { cancelAnimationFrame(frame); removeEventListener('resize', measure); };
  }, []);
  const keyDown = (event: KeyboardEvent) => {
    const columns = getComputedStyle(group.current!).gridTemplateColumns.split(' ').length;
    const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns }[event.key];
    const count = PROJECT_FOLDERS.length;
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? count - 1 : step === undefined ? -1 : Math.min(Math.max((at < 0 ? 0 : at) + step, 0), count - 1);
    if (next < 0) return;
    event.preventDefault();
    onChange(PROJECT_FOLDERS[next].id);
    group.current?.querySelectorAll<HTMLButtonElement>('[role=radio]')[next]?.focus();
  };
  return <div className="ws-folder-choice">
    <span className="ws-shapes-title" id={titleId}>{t('#HomeChooseRoomType')}</span>
    <div ref={group} className={'ws-folder-grid' + (more ? ' has-more' : '')} role="radiogroup" aria-labelledby={titleId} onKeyDown={keyDown} onScroll={measure}>
      {PROJECT_FOLDERS.map((folder, index) => <button key={folder.id} type="button" role="radio" className="ws-folder-option" aria-checked={folder.id === value}
        tabIndex={folder.id === value || at < 0 && index === 0 ? 0 : -1} onClick={() => onChange(folder.id)}>
        <span>{folder.title}</span>{folder.id === current && <small>{t('#HomeFolderCurrent')}</small>}
        <span className="ws-folder-tick" aria-hidden="true"><Icon name="check" size={14} /></span>
      </button>)}
    </div>
  </div>;
}
